import React from 'react';

import { useUIColors } from '../../theme';
import { cx, defineRecipe, useRecipe, type Extensible, type Styleable } from '../../variants';

export type BulkActionTone = Extensible<'default' | 'danger'>;

export const bulkActionBarRecipe = defineRecipe('bulkActionBar', 'ui-bulk-bar', {
    variants: {
        tone: { default: 'ui-bulk-bar--default', danger: 'ui-bulk-bar--danger' },
    },
    defaultVariants: { tone: 'default' },
});

export interface BulkAction {
    key: string;
    label: React.ReactNode;
    onClick: () => void;
    tone?: BulkActionTone;
    disabled?: boolean;
}

export interface BulkActionBarProps extends Styleable {
    /**
     * How many rows are selected. The bar renders NOTHING at `0` — a bar that
     * is always present but empty pushes the table down by 44px for no reason,
     * and the handoff only ever draws it with a selection in it.
     */
    count: number;
    /** e.g. "Đã chọn 2 dòng" — the caller formats it, plurals included. */
    countLabel: React.ReactNode;
    /** e.g. "Áp dụng cho cả 2:" — the lead-in before the action pills. */
    applyLabel?: React.ReactNode;
    actions: BulkAction[];
    /** Right-aligned "Bỏ chọn". Omit to render no clear affordance. */
    onClear?: () => void;
    clearLabel?: React.ReactNode;
    /** Accessible name for the bar itself. */
    label?: string;
    /**
     * EAA-1817: anything the uniform `actions` pill cannot be — a
     * popover-backed action whose trigger opens a choice, or a note that
     * qualifies the count. Rendered after `actions`, before the spacer, so it
     * stays inside the action row rather than being pushed to the far edge
     * where the clear affordance lives.
     *
     * `actions` remains the way to add an ACTION; this is the exception slot,
     * not an alternative to it.
     */
    children?: React.ReactNode;
}

/**
 * EAA-1817: the selection bar every console list grows once rows are ticked —
 * "N selected", a row of actions that apply to all of them, and a clear.
 * Design frames 56 · 62 ("Bảng dữ liệu — đủ bộ điều khiển"), read as raw HTML.
 *
 * WHY IT IS IN THE KIT. Two screens had already hand-written it —
 * `AccountsBulkBar` and `RoleMapBulkBar` — and they had already drifted: one
 * used `<button>`s with an app class, the other a mix of spans and buttons, and
 * only one of them disabled its actions while a bulk mutation was in flight.
 * `DataTable` owns selection (`rowSelection`), so the surface that ACTS on a
 * selection belongs beside it rather than being re-derived per list.
 *
 * WHY THE ACTIONS ARE A PROP AND NOT `children`. Every action in every frame is
 * the same pill; what differs is the label, the handler and whether it is
 * destructive. An array states that table, and it keeps the bar's own layout
 * (count · divider · lead-in · pills · spacer · clear) out of the caller's
 * hands — which is exactly what the two hand-written copies got wrong in two
 * different ways. Anything genuinely bespoke goes in `applyLabel`, which takes
 * a node.
 *
 * `role="region"` + `aria-live="polite"`: the bar APPEARS on selection, so a
 * screen-reader user who ticks a checkbox needs to be told a new set of
 * controls just arrived; without the live region the actions exist silently.
 */
export function BulkActionBar({
    count,
    countLabel,
    applyLabel,
    actions,
    onClear,
    clearLabel,
    label,
    children,
    className,
    style,
}: BulkActionBarProps): React.ReactElement | null {
    const colors = useUIColors();
    const rootClassName = useRecipe(bulkActionBarRecipe, {});

    if (count <= 0) return null;

    return (
        <div
            className={cx(rootClassName, className)}
            role="region"
            aria-live="polite"
            aria-label={label}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
                padding: '8px 14px',
                borderRadius: 8,
                // A tinted fill plus an inset ring, not a border: the bar
                // appears and disappears with the selection, and a border would
                // shift the table by 2px every time it did.
                background: colors.ctaLight,
                boxShadow: `inset 0 0 0 1px ${colors.primary}40`,
                ...style,
            }}
        >
            <span
                style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: colors.sidebarMenuSelectedText,
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {countLabel}
            </span>

            <span
                aria-hidden="true"
                style={{
                    width: 1,
                    height: 16,
                    flex: 'none',
                    background: `${colors.primary}40`,
                }}
            />

            {applyLabel !== undefined && (
                <span style={{ fontSize: 13, color: colors.textSecondary }}>{applyLabel}</span>
            )}

            {actions.map((action) => {
                const danger = action.tone === 'danger';
                return (
                    <button
                        key={action.key}
                        type="button"
                        className={`ui-bulk-bar__action${danger ? ' ui-bulk-bar__action--danger' : ''}`}
                        disabled={action.disabled}
                        onClick={action.onClick}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            height: 28,
                            padding: '0 10px',
                            border: 'none',
                            borderRadius: 6,
                            background: colors.surface,
                            boxShadow: `inset 0 0 0 1px ${danger ? colors.error : colors.border}`,
                            color: danger ? colors.errorText : colors.text,
                            font: 'inherit',
                            fontSize: 13,
                            fontWeight: 500,
                            cursor: action.disabled ? 'not-allowed' : 'pointer',
                            opacity: action.disabled ? 0.55 : 1,
                        }}
                    >
                        {action.label}
                    </button>
                );
            })}

            {children}

            <span style={{ flex: 1 }} />

            {onClear && (
                <button
                    type="button"
                    className="ui-bulk-bar__clear"
                    onClick={onClear}
                    style={{
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        font: 'inherit',
                        fontSize: 13,
                        fontWeight: 500,
                        color: colors.textMuted,
                        cursor: 'pointer',
                    }}
                >
                    {clearLabel}
                </button>
            )}
        </div>
    );
}

export default BulkActionBar;
